import { Flex } from '@mantine/core';
import type { WithT } from 'i18next';
import numeral from 'numeral';
import type { JSX } from 'react';
import React from 'react';

import { type DonationForm } from '@feathr/blackbox';
import { Button, type IColumn, TableColumnHeader, Text } from '@feathr/components';
import GrowthCreditIcon from '@feathr/extender/components/GrowthCreditIcon/GrowthCreditIcon';
import { moment, TimeFormat } from '@feathr/hooks';

import * as tableStyles from '@feathr/components/dist/Table/Table.css';
import * as styles from './growthCreditsFormsColumns.css';

export function growthCreditsFormsColumns({
  t,
  localUrl,
}: WithT & { localUrl: (url: string) => string }): Array<IColumn<DonationForm>> {
  return [
    {
      id: 'name',
      Header: TableColumnHeader({
        title: t('Form name'),
        sortType: 'alpha',
      }),
      headerClassName: tableStyles.sort,
      Cell({ original }): JSX.Element {
        const dateLastDonation = original.get('stats_cache')?.date_last_donation;
        const formattedDate =
          dateLastDonation && moment(dateLastDonation).year() > 1
            ? moment.utc(dateLastDonation).format(TimeFormat.longDate)
            : undefined;

        return (
          <Flex direction={'column'} gap={4}>
            <Button
              className={styles.link}
              link={localUrl(original.getItemUrl('edit'))}
              type={'link'}
            >
              {original.name}
            </Button>
            {formattedDate && (
              <Text.SmallRegular>
                {t('Last donation {{date}}', { date: formattedDate })}
              </Text.SmallRegular>
            )}
          </Flex>
        );
      },
    },
    {
      id: 'stats_cache__growth_credits',
      Header: TableColumnHeader({
        title: t('Credits earned'),
        sortType: 'numeric',
      }),
      headerClassName: tableStyles.sort,
      className: tableStyles.cellRight,
      sortable: true,
      width: 170,
      Cell({ original }): JSX.Element {
        const growthCredits = original.get('stats_cache')?.growth_credits ?? 0;
        return (
          <Flex align={'center'} gap={8} justify={'flex-end'}>
            <Text.DefaultRegular>{growthCredits.toLocaleString()}</Text.DefaultRegular>
            <GrowthCreditIcon />
          </Flex>
        );
      },
    },
    {
      id: 'stats_cache__donations_total',
      Header: TableColumnHeader({
        title: t('Donation Value'),
        sortType: 'numeric',
      }),
      headerClassName: tableStyles.sort,
      className: tableStyles.cellRight,
      sortable: true,
      width: 150,
      Cell({ original }): JSX.Element {
        const total = original.get('stats_cache')?.donations_total ?? 0;
        return <>{numeral(total).format('$0,0.00')}</>;
      },
    },
    {
      id: 'stats_cache__donations_count',
      Header: TableColumnHeader({
        title: t('Donations'),
        sortType: 'numeric',
      }),
      headerClassName: tableStyles.sort,
      className: tableStyles.cellRight,
      sortable: true,
      width: 120,
      Cell({ original }): JSX.Element {
        return <>{(original.get('stats_cache')?.donations_count ?? 0).toLocaleString()}</>;
      },
    },
  ];
}
